import { useContext, useState } from "react";
import { useParams } from "react-router-dom";

import { useQuery } from "@apollo/client";

import { FETCH_RATINGS_SUMMARY_BY_RECIPE_ID, FETCH_REVIEWS_BY_RECIPE_ID } from "../app/graphql/queries";
import { RatingSummary, RecipeReviewInterface } from "../app/recipePage/recipePage.types";
import { sanitizeRatingsSummary, sanitizeReviewsData } from "../pages/RecipeReviewPage/utils/sanitizeReviewsData";
import { ErrorManagementContext } from "../utils/errorManagement/ErrorManagementProvider";

const sixtyDaysAgoISOString = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();

const useRecipeReviews = () => {
  const { recipeId: urlRecipeId } = useParams<{recipeId: string}>();

  const { cookClubLog } = useContext(ErrorManagementContext);

  const [ratingsSummary, setRatingSummary] = useState<RatingSummary>({
    avgRating: 0,
    ratingCount: 0,
    feedbacks: []
  });

  const [reviews, setReviews] = useState<RecipeReviewInterface[]>([]);

  const { loading: ratingsLoading } = useQuery(FETCH_RATINGS_SUMMARY_BY_RECIPE_ID, {
    variables: { recipeId: urlRecipeId },
    onCompleted: (data) => {
      const ratingsSummaryData = data?.ratingsSummaryLast60DaysView?.[0];
      const sanitizedRatingsSummary = sanitizeRatingsSummary(ratingsSummaryData);
      setRatingSummary(sanitizedRatingsSummary);
    },
    onError: (err) => {
      cookClubLog(
        "",
        "Unable to fetch Ratings!",
        '',
        { ui: { type: "error" }, browserConsole: true })
      console.error('Error fetching the ratings:', err);
    }
  })

  const { loading: reviewsLoading } = useQuery(FETCH_REVIEWS_BY_RECIPE_ID, {
    skip: ratingsSummary?.avgRating === 0 && ratingsLoading,
    variables: { recipeId: urlRecipeId, startTime_60daysAgo: sixtyDaysAgoISOString },
    onCompleted: (data) => {
      const sanitizedData = sanitizeReviewsData(data?.Sessions_DB);
      setReviews(sanitizedData);
    },
    onError: (err) => {
      cookClubLog(
        "",
        "Unable to fetch Reviews!",
        '',
        { ui: { type: "error" }, browserConsole: true })
      console.error('Error fetching the reviews:', err);
    }
  })

  return { ratingsSummary, reviews, recipeName: reviews[0]?.sessionRecipe?.name, loading: ratingsLoading || reviewsLoading };
};

export default useRecipeReviews;